// src/pages/Dashboard.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  UserPlus, IndianRupee, Calendar, Users, Briefcase, Search, Phone, Mail, MapPin,
  BookOpen, ChevronLeft, ChevronRight, TrendingUp, AlertCircle, CheckCircle, Clock, X
} from 'lucide-react';
import SummaryApi from '../common/index';

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [todayRegistrations, setTodayRegistrations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(5);
  const [stats, setStats] = useState([]);
  // Modal state
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [selectedStudentForVerify, setSelectedStudentForVerify] = useState(null);
  const [receiveReferenceNumber, setReceiveReferenceNumber] = useState('');

  const displayValue = (value) => {
    if (value === null || value === undefined || value === '' || value === '0' || value === 0) {
      return 'N/A';
    }
    return value;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "0000-00-00" || dateString === "N/A") return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return "N/A";
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString || dateString === "N/A") return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return "N/A";
    }
  };

  const formatTime = (dateString) => {
    if (!dateString || dateString === "N/A") return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "N/A";
      return date.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return "N/A";
    }
  };

  const safeNumber = (val) => {
    const num = Number(val);
    return isNaN(num) ? 0 : num;
  };

  useEffect(() => {
    const adminId = sessionStorage.getItem('admin_id');
    const memberType = sessionStorage.getItem('member_type');
    if (!adminId) {
      toast.error("Please login again");
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const isAdmin = memberType === 'Admin';

        const summaryRes = await fetch(SummaryApi.getDashboardSummary.url, {
          method: isAdmin ? 'GET' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          ...( !isAdmin && { body: JSON.stringify({ admin_id: adminId }) })
        });
        if (!summaryRes.ok) throw new Error(`Summary fetch failed: ${summaryRes.status}`);
        const summaryJson = await summaryRes.json();

        let summaryData = {
          today_students: 0,
          total_students: 0,
          total_fee_collected: 0,
          total_billing_students: 0,
          total_consultants: 0
        };
        if (summaryJson.status && summaryJson.data?.summary) {
          summaryData = summaryJson.data.summary;
        }

        const baseStats = [
          {
            title: "Today's Registrations",
            value: safeNumber(summaryData.today_students).toLocaleString('en-IN'),
            icon: UserPlus,
            gradient: "from-emerald-500 via-green-500 to-emerald-600",
          },
          {
            title: "Total Students",
            value: safeNumber(summaryData.total_students).toLocaleString('en-IN'),
            icon: Users,
            gradient: "from-blue-500 via-indigo-500 to-blue-600",
          },
          {
            title: "Fees Collected Total",
            value: `₹${safeNumber(summaryData.total_fee_collected).toLocaleString('en-IN')}`,
            icon: IndianRupee,
            gradient: "from-purple-500 via-violet-500 to-violet-600",
          },
          {
            title: "Total Paid Students",
            value: safeNumber(summaryData.total_billing_students).toLocaleString('en-IN'),
            icon: Calendar,
            gradient: "from-amber-500 via-orange-500 to-amber-600",
          }
        ];

        if (isAdmin) {
          baseStats.push({
            title: "Total Consultants",
            value: safeNumber(summaryData.total_consultants).toLocaleString('en-IN'),
            icon: Briefcase,
            gradient: "from-rose-500 via-pink-500 to-rose-600",
          });
        }

        setStats(baseStats);

        const todayApi = isAdmin
          ? SummaryApi.getTodayRegistrations
          : SummaryApi.getTodayRegistrationsByadminid;

        const todayRes = await fetch(todayApi.url, {
          method: todayApi.method,
          headers: { 'Content-Type': 'application/json' },
          ...( !isAdmin && { body: JSON.stringify({ admin_id: adminId }) })
        });

        if (!todayRes.ok) {
          throw new Error(`Today's registrations fetch failed: ${todayRes.status}`);
        }

        const todayJson = await todayRes.json();

        if (todayJson.status && Array.isArray(todayJson.data)) {
          if (todayJson.data.length === 0) {
            setTodayRegistrations([]);
            return;
          }

          const grouped = todayJson.data.reduce((acc, item) => {
            const sid = item.student_id;
            acc[sid] = acc[sid] || [];
            acc[sid].push(item);
            return acc;
          }, {});

          const formatted = Object.values(grouped).map((group) => {
            const latestBilling = group.reduce((latest, curr) => {
              const currDate = new Date(curr.billing_created_date || '1900-01-01');
              const latestDate = new Date(latest.billing_created_date || '1900-01-01');
              return currDate > latestDate ? curr : latest;
            }, group[0]);

            const base = group[0];

            return {
              id: base.student_id,
              name: base.full_name || base.student_name || 'N/A',
              mobile: base.mobile_number || 'N/A',
              email: base.email || 'N/A',
              preferred_branch: base.btech_preffered_branch || base.preferred_branch || base.branch || base.course || base.twelfth_branch || 'N/A',
              year: base.year || base.twelfth_year_passing || 'N/A',
              dob: formatDate(base.dob),
              aadhar: base.aadhar || 'N/A',
              address: [base.address_line1, base.city, base.district, base.state, base.pincode]
                .filter(Boolean)
                .join(', ') || 'N/A',
              registration_date: formatDate(base.created_date),
              registration_time: formatTime(base.created_date),
              receipt_date: latestBilling.receipt_date
                ? formatDate(latestBilling.receipt_date)
                : (latestBilling.billing_created_date ? formatDate(latestBilling.billing_created_date) : 'N/A'),
              amount: latestBilling.amount ? `₹${safeNumber(latestBilling.amount).toLocaleString('en-IN')}` : 'N/A',
              consultancy_name: base.consultancy_name || base.consultant_name || 'Direct',
              payment_id: latestBilling.payment_id,
              is_paid: latestBilling.is_paid || "0",
              is_payment_complete: latestBilling.is_payment_complete || base.is_payment_complete || "0",
              payment_receive_reference_number: latestBilling.payment_receive_reference_number || base.payment_receive_reference_number || 'N/A',
              payment_updated_date: latestBilling.payment_updated_date || base.payment_updated_date || 'N/A',
            };
          });

       setTodayRegistrations(formatted.reverse());
        } else {
          setTodayRegistrations([]);
        }
      } catch (err) {
        console.error("Dashboard fetch error:", err);
        toast.error("Failed to load dashboard data");

        const isAdmin = memberType === 'Admin';
        const fallbackStats = [
          { title: "Today's Registrations", value: "0", icon: UserPlus, gradient: "from-emerald-500 via-green-500 to-emerald-600" },
          { title: "Total Students", value: "0", icon: Users, gradient: "from-blue-500 via-indigo-500 to-blue-600" },
          { title: "Fees Collected Total", value: "₹0", icon: IndianRupee, gradient: "from-purple-500 via-violet-500 to-violet-600" },
          { title: "Total Paid Students", value: "0", icon: Calendar, gradient: "from-amber-500 via-orange-500 to-amber-600" },
        ];

        if (isAdmin) {
          fallbackStats.push({
            title: "Total Consultants",
            value: "0",
            icon: Briefcase,
            gradient: "from-rose-500 via-pink-500 to-rose-600",
          });
        }

        setStats(fallbackStats);
        setTodayRegistrations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const filteredRegistrations = todayRegistrations.filter((student) => {
    const query = searchQuery.toLowerCase();
    return (
      (student.name?.toLowerCase() || "").includes(query) ||
      (student.mobile?.toString() || "").includes(query)
    );
  });

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRegistrations.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredRegistrations.length / recordsPerPage);

  const isAdmin = sessionStorage.getItem('member_type') === 'Admin';

  const handlePayNow = (student) => {
    if (!student.id) {
      toast.error("Cannot process payment: Student ID missing");
      return;
    }
    const encodedId = btoa(student.id.toString());
    const encodedName = btoa((student.name || 'Unknown Student').trim());
    navigate(`/create-studentfee?id=${encodedId}&name=${encodedName}`);
  };

  const openVerifyModal = (student) => {
    setSelectedStudentForVerify(student);
    setReceiveReferenceNumber('');
    setShowVerifyModal(true);
  };

  const handleVerifyPayment = async () => {
    if (!receiveReferenceNumber.trim()) {
      toast.error("Please enter Receive Reference Number");
      return;
    }
    if (!selectedStudentForVerify?.payment_id) {
      toast.error("Payment ID missing");
      return;
    }

    try {
      const response = await fetch(SummaryApi.paymentcompletedupdate.url, {
        method: SummaryApi.paymentcompletedupdate.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          payment_id: selectedStudentForVerify.payment_id,
          is_payment_complete: "1",
          receive_reference_number: receiveReferenceNumber.trim()
        }),
      });

      const result = await response.json();

      if (result.status === true) {
        toast.success("Payment verified successfully");
        // Update UI immediately
        setTodayRegistrations(prev =>
          prev.map(item =>
            item.id === selectedStudentForVerify.id
              ? {
                  ...item,
                  is_payment_complete: "1",
                  payment_receive_reference_number: receiveReferenceNumber.trim(),
                  payment_updated_date: new Date().toISOString()
                }
              : item
          )
        );
        // Close modal
        setShowVerifyModal(false);
        setReceiveReferenceNumber('');
        setSelectedStudentForVerify(null);
      } else {
        toast.error(result.message || "Verification failed");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4 md:p-6 space-y-6">
      {loading && (
        <div className="fixed inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white/60 backdrop-blur-sm rounded-lg shadow border border-white/30 p-4 text-center">
        <h1 className="text-2xl md:text-3xl font-black bg-gradient-to-r from-gray-900 to-slate-800 bg-clip-text text-transparent">
          Dashboard
        </h1>
      </div>

      {/* Stats Cards */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${stats.length === 5 ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-4`}>
        {stats.map((card, i) => (
          <div
            key={i}
            className={`group overflow-hidden rounded-xl shadow-md border bg-gradient-to-br ${card.gradient} text-white p-5 transition-all hover:scale-[1.03]`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 rounded-lg bg-white/20 backdrop-blur-md border border-white/30">
                <card.icon size={24} strokeWidth={2.5} />
              </div>
            </div>
            <p className="text-sm font-semibold opacity-80">{card.title}</p>
            <p className="text-2xl font-black tracking-tight">{card.value}</p>
          </div>
        ))}
      </div>

      {/* Today's Registrations Table */}
      <div className="bg-white/70 backdrop-blur-xl rounded-xl shadow-lg border border-white/30 overflow-hidden">
        <div className="bg-slate-900 p-4 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-emerald-500 rounded-lg text-white">
              <TrendingUp size={20} />
            </div>
            <h3 className="text-xl font-bold text-white">Today's Registrations</h3>
          </div>
          <div className="relative w-full md:w-96">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search Name, Mobile"
              className="w-full pl-10 pr-4 py-2 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 font-bold text-slate-700">S.No</th>
                <th className="px-4 py-3 font-bold text-slate-700">Student Details</th>
                <th className="px-4 py-3 font-bold text-slate-700">Contact Info</th>
                <th className="px-4 py-3 font-bold text-slate-700">B.Tech Preferred Branch</th>
                {isAdmin && <th className="px-4 py-3 font-bold text-slate-700">Consultant</th>}
                <th className="px-4 py-3 font-bold text-slate-700">Registration</th>
                <th className="px-4 py-3 font-bold text-slate-700">Payment Status / Action</th>
                {isAdmin && <th className="px-4 py-3 font-bold text-slate-700">Payment Updated</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {currentRecords.length > 0 ? (
                currentRecords.map((student, index) => (
                  <tr key={student.id} className="hover:bg-emerald-50/50 bg-white/40 transition-colors">
                    <td className="px-4 py-4 font-bold text-emerald-700">
                      {(currentPage - 1) * recordsPerPage + index + 1}
                    </td>
                    <td className="px-4 py-4">
                      <div className="font-bold text-slate-900">{displayValue(student.name)}</div>
                      <div className="text-xs text-slate-500">
                        <BookOpen size={10} className="inline mr-1"/>DOB: {displayValue(student.dob)}
                      </div>
                      <div className="text-xs text-slate-400">Aadhar: {displayValue(student.aadhar)}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center text-slate-700 font-medium">
                        <Phone size={12} className="mr-1 text-slate-400 shrink-0"/>
                        {displayValue(student.mobile)}
                      </div>
                      <div className="flex items-center text-xs text-slate-500 mt-1">
                        <Mail size={12} className="mr-1 text-slate-400 shrink-0"/>
                        <span className="truncate max-w-[150px]">{displayValue(student.email)}</span>
                      </div>
                      <div className="flex items-start text-xs text-slate-500 mt-1">
                        <MapPin size={12} className="mr-1 text-slate-400 shrink-0 mt-0.5"/>
                        <span className="line-clamp-1">{displayValue(student.address) || "N/A"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="font-medium text-slate-800">{displayValue(student.preferred_branch)}</div>
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-4">
                        <div className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-bold border border-blue-100">
                          <Briefcase size={10} className="mr-1"/>
                          {displayValue(student.consultancy_name)}
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-4">
                      <div className="font-medium text-slate-900">{displayValue(student.registration_date)}</div>
                      <div className="text-xs text-slate-500">{displayValue(student.registration_time)}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-col items-start gap-2">
                        {isAdmin ? (
                          student.is_payment_complete == "1" ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                              <CheckCircle size={14} className="mr-1" />
                              Verified
                            </span>
                          ) : student.is_paid == "1" ? (
                            <div className="flex flex-col items-start gap-2">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800 border border-blue-200">
                                <IndianRupee size={14} className="mr-1" />
                                Paid
                              </span>
                              <button
                                onClick={() => openVerifyModal(student)}
                                className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded transition shadow-sm"
                              >
                                Verify
                              </button>
                            </div>
                          ) : (
                            <div className="flex flex-col items-start gap-2">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                <Clock size={14} className="mr-1" />
                                Pending
                              </span>
                              <button
                                onClick={() => handlePayNow(student)}
                                className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-4 py-1.5 rounded transition shadow-sm"
                              >
                                Pay Now
                              </button>
                            </div>
                          )
                        ) : (
                          student.is_paid == "1" ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800 border border-blue-200">
                              <IndianRupee size={14} className="mr-1" />
                              Paid
                            </span>
                          ) : (
                            <div className="flex flex-col items-start gap-2">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                <Clock size={14} className="mr-1" />
                                Pending
                              </span>
                              <button
                                onClick={() => handlePayNow(student)}
                                className="text-xs bg-amber-600 hover:bg-amber-700 text-white px-4 py-1.5 rounded transition shadow-sm"
                              >
                                Pay Now
                              </button>
                            </div>
                          )
                        )}
                      </div>
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-4 text-sm">
                        <div className="space-y-1">
                          <div>
                            <span className="font-medium text-slate-700">Ref No:</span>{' '}
                            {displayValue(student.payment_receive_reference_number)}
                          </div>
                          <div className="text-xs text-slate-500">
                            {student.is_payment_complete == "1"
                              ? formatDateTime(student.payment_updated_date)
                              : 'Not updated'}
                          </div>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={isAdmin ? 8 : 7} className="px-4 py-16 text-center bg-white/30">
                    <div className="flex flex-col items-center justify-center space-y-3">
                      <div className="p-4 bg-slate-100 rounded-full text-slate-400">
                        <AlertCircle size={48} strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-lg font-bold text-slate-600">No Registrations Found</p>
                        <p className="text-sm text-slate-400">No student records match your search or today's list.</p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filteredRegistrations.length > 0 && (
          <div className="p-4 border-t flex flex-col md:flex-row justify-between items-center gap-4 bg-white/50">
            <p className="text-sm text-slate-600">
              Showing <span className="font-semibold">{indexOfFirstRecord + 1}</span> to{' '}
              <span className="font-semibold">{Math.min(indexOfLastRecord, filteredRegistrations.length)}</span> of{' '}
              <span className="font-semibold">{filteredRegistrations.length}</span>
            </p>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 border rounded-lg bg-white disabled:opacity-50 hover:bg-slate-50 transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`w-8 h-8 rounded-lg font-medium transition-all ${
                    currentPage === i + 1
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : 'bg-white border text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 border rounded-lg bg-white disabled:opacity-50 hover:bg-slate-50 transition-colors"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Verify Payment Modal - same style as in StudentReport */}
      {showVerifyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Verify Payment - {displayValue(selectedStudentForVerify?.name)}
              </h3>
              <button
                onClick={() => {
                  setShowVerifyModal(false);
                  setReceiveReferenceNumber('');
                  setSelectedStudentForVerify(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Receive Reference Number
              </label>
              <input
                type="text"
                value={receiveReferenceNumber}
                onChange={(e) => setReceiveReferenceNumber(e.target.value)}
                placeholder="Enter reference number"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowVerifyModal(false);
                  setReceiveReferenceNumber('');
                  setSelectedStudentForVerify(null);
                }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleVerifyPayment}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
              >
                Submit & Verify
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}